import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import TryItDrawer from './TryItDrawer';

// A header-launched "Try it" drawer, mounted ONCE outside the screen's <Routes>
// so a stream in flight survives navigation. Any panel can open it (e.g. an
// empty tests state suggesting "try a question first").

interface TryItDrawerContextType {
  openTryIt: (initialQuestion?: string) => void;
  closeTryIt: () => void;
  isOpen: boolean;
}

export const TryItDrawerContext = createContext<TryItDrawerContextType>({
  openTryIt: () => {
    throw new Error('openTryIt used outside TryItDrawerProvider');
  },
  closeTryIt: () => {
    throw new Error('closeTryIt used outside TryItDrawerProvider');
  },
  isOpen: false,
});

export const useTryItDrawer = (): TryItDrawerContextType =>
  useContext(TryItDrawerContext);

const TryItDrawerProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialQuestion, setInitialQuestion] = useState<string | undefined>();
  // Bumping the key remounts TryItPanel so a fresh open starts from the new
  // initialQuestion (the drawer itself stays mounted for the stream's sake).
  const [openCount, setOpenCount] = useState<number>(0);

  const openTryIt = useCallback((question?: string) => {
    setInitialQuestion(question);
    setOpenCount((count) => count + 1);
    setIsOpen(true);
  }, []);

  const closeTryIt = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ openTryIt, closeTryIt, isOpen }),
    [openTryIt, closeTryIt, isOpen],
  );

  return (
    <TryItDrawerContext.Provider value={value}>
      {children}
      <TryItDrawer
        isOpen={isOpen}
        close={closeTryIt}
        panelKey={openCount}
        initialQuestion={initialQuestion}
      />
    </TryItDrawerContext.Provider>
  );
};

export default TryItDrawerProvider;
